import { memo, useMemo } from 'react'
import { ChevronRight, Loader2, Sparkles } from 'lucide-react'
import type { ScanSession } from '@/screens/scan/types'
import { ERROR, FAIL, PASS, formatWhen, segments, tally } from '@/screens/scan/lib/sessionDisplay'

const Count = ({ color, value }: { color: string; value: number }) => (
  <span className='ramp-scan-count'>
    <span className='ramp-scan-count-dot' style={{ background: color }} />
    {value}
  </span>
)

/**
 * One scan run as a history card: date + a verdict summary bar with per-status
 * counts. Selecting the card opens the run detail screen.
 */
function SessionCardComponent({
  session,
  current,
  onSelect
}: {
  session: ScanSession
  current: boolean
  onSelect: () => void
}) {
  const counts = useMemo(() => tally(session.results), [session.results])
  const isPending = counts.pending > 0
  const total = counts.pass + counts.fail + counts.error

  return (
    <div className={`ramp-scan-session${current ? ' is-current' : ''}`}>
      <button type='button' className='ramp-scan-session-head' onClick={onSelect}>
        <span className='ramp-scan-session-icon'>
          <Sparkles size={14} />
        </span>
        <span className='ramp-scan-session-main'>
          <span className='ramp-scan-session-when'>
            {formatWhen(session.createdAt)}
            {current ? <span className='ramp-scan-session-badge'>Current</span> : null}
          </span>
          {isPending ? (
            <span className='ramp-scan-bar is-pending'>
              <span className='ramp-scan-bar-fill' />
            </span>
          ) : (
            <span className='ramp-scan-bar'>
              {segments(counts).map(({ color, count }) => (
                <span key={color} style={{ background: color, flexGrow: count }} />
              ))}
            </span>
          )}
        </span>
        <span className='ramp-scan-session-counts'>
          {isPending ? (
            <Loader2 size={12} className='ramp-spin' />
          ) : (
            <>
              {counts.fail ? <Count color={FAIL} value={counts.fail} /> : null}
              {counts.pass ? <Count color={PASS} value={counts.pass} /> : null}
              {counts.error ? <Count color={ERROR} value={counts.error} /> : null}
              {total === 0 ? <span className='ramp-scan-session-empty'>0</span> : null}
            </>
          )}
          <ChevronRight size={14} className='ramp-scan-session-chevron' />
        </span>
      </button>
    </div>
  )
}

export const SessionCard = memo(SessionCardComponent)
